import { IBooleanObject, ITier, ITierMappedProduct, ITierProduct, ITierProducts, ITierProductsErrorsParams } from "./interfaces";

const isSomeProductDefined = (products: ITierProduct[] = []) => products.some((item) => item.value);

const getProductErrorObject = (products: string[] = []) =>
	products.reduce((acc: { [key: string]: boolean }, product: string) => {
		acc[product] = false;
		return { ...acc };
	}, {});

const getProductsDefinitions = (products: ITierMappedProduct | null, identifiers: string[] = []) => {
	if (!products) {
		return null;
	}

	return identifiers.reduce((acc: { [key: string]: boolean }, product: string) => {
		acc[product] = isSomeProductDefined(products[product]);
		return { ...acc };
	}, {});
};

const getTiersProducts = (tiers: ITier[] = [], identifier = "", product = "") =>
	tiers.reduce((products: ITierProduct[], tier: ITierProducts) => {
		const tierProduct = ((tier?.[identifier as keyof ITierProducts] as ITierProduct[]) || []).find(
			(item: ITierProduct) => item.product === product
		);
		return tierProduct ? [...products, tierProduct] : products;
	}, []);

const getPresentProducts = (products: string[] = [], tiers: ITier[] = [], identifier = "") => {
	if (!identifier || !tiers?.length || !products.length) {
		return null;
	}
	return products.reduce((acc: ITierMappedProduct, product: string) => {
		const tierProducts = getTiersProducts(tiers, identifier, product);
		acc[product] = tierProducts;
		return { ...acc };
	}, {});
};

const isValidValues = (v1: number, v2: number) => v2 >= v1;

const fillSequenceErrors = (
	errors: IBooleanObject[] = [],
	products: ITierProduct[] = [],
	args: { definitions: IBooleanObject; product: string } | undefined = undefined
) => {
	const { definitions = {}, product = "" } = args || {};

	if (!products.length || !product) {
		return errors;
	}

	const defined = definitions[product];

	for (let i = 0; i < products.length; i++) {
		const current = products[i];
		const next = products[i + 1];

		if (current && next) {
			const isValid = isValidValues(current?.value, next?.value); // If next >= current
			errors[i] = { ...errors?.[i], [product]: !isValid || !defined };
			errors[i + 1] = { ...errors?.[i], [product]: !isValid || !defined };
		}
	}

	return [...errors];
};

const fillErrors = (
	errors: IBooleanObject[] = [],
	args: { products: ITierMappedProduct | null; definitions: IBooleanObject; labelsProducts: string[] } | undefined = undefined
) => {
	const { products = null, definitions = {}, labelsProducts = [] } = args || {};

	for (const product of labelsProducts) {
		const row = products?.[product];
		errors = fillSequenceErrors(errors, row, { product, definitions });
	}

	return errors;
};

const getProductsErrors = (params: ITierProductsErrorsParams) => {
	const { identifier = "", tiers = [], products = [] } = params || {};
	const labelsProducts = products.map((item) => item.label);
	if (!tiers?.length || !labelsProducts.length) {
		return [];
	}

	let errors = Array(tiers.length).fill(getProductErrorObject(labelsProducts));
	const checkProducts = getPresentProducts(labelsProducts, tiers, identifier);
	const definitions = getProductsDefinitions(checkProducts, labelsProducts);

	if (!definitions) {
		return [];
	}

	errors = fillErrors(errors, { products: checkProducts, labelsProducts, definitions });
	return [...errors];
};

export { isSomeProductDefined, getProductsErrors };
